import { useState, useCallback } from 'react';

interface UseCSVExportOptions {
  filename: string;
  headers: string[];
}

interface UseCSVExportReturn {
  exportCSV: (data: any[], options: UseCSVExportOptions) => Promise<void>;
  isExporting: boolean;
  error: string | null;
}

/**
 * Custom hook for CSV export functionality
 * Handles client-side CSV generation and file download
 */
export const useCSVExport = (): UseCSVExportReturn => {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Escapes CSV field values according to RFC 4180 and sanitizes non-printable characters
   * @param value - The value to escape
   * @returns Escaped value safe for CSV
   */
  const escapeCSVField = (value: any): string => {
    if (value === null || value === undefined) {
      return '';
    }

    let stringValue = String(value);

    // Clean up non-printable and problematic characters
    stringValue = stringValue
      // Remove null bytes and other control characters
      .replace(/\u0000/g, '') // Null byte
      .replace(/\uFEFF/g, '') // BOM (Byte Order Mark)
      .replace(/[\u200B-\u200D]/g, '') // Zero-width spaces
      .replace(/[\u00AD]/g, '') // Soft hyphens
      // Remove other problematic whitespace and control characters (except normal spaces, tabs, newlines)
      .replace(/[\u0001-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
      .trim(); // Remove leading/trailing whitespace

    // If the field contains comma, quote, or newline, wrap it in quotes
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
      // Escape existing quotes by doubling them
      const escapedValue = stringValue.replace(/"/g, '""');
      return `"${escapedValue}"`;
    }

    return stringValue;
  };

  /**
   * Converts array of objects to CSV string
   * @param data - Array of data objects
   * @param headers - Array of column headers
   * @returns CSV string
   */
  const convertToCSV = (data: any[], headers: string[]): string => {
    if (!data || data.length === 0) {
      return headers.join(',');
    }

    // Create header row
    const headerRow = headers.map(header => escapeCSVField(header)).join(',');

    // Create data rows
    const dataRows = data.map(row => {
      return headers.map(header => {
        // Handle nested field access (e.g., 'user.name')
        const keys = header.split('.');
        let value = row;

        for (const key of keys) {
          value = value?.[key];
          if (value === undefined || value === null) break;
        }

        return escapeCSVField(value);
      }).join(',');
    });

    return [headerRow, ...dataRows].join('\n');
  };

  /**
   * Generates timestamp for filename
   * @returns Formatted timestamp string (YYYY-MM-DD-HHMMSS)
   */
  const generateTimestamp = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}-${hours}${minutes}${seconds}`;
  };

  /**
   * Triggers file download in browser
   * @param csvContent - CSV content string
   * @param filename - Base filename
   */
  const downloadFile = (csvContent: string, filename: string): void => {
    const timestamp = generateTimestamp();
    const fullFilename = `${filename}-${timestamp}.csv`;

    // Add UTF-8 BOM for better Excel compatibility and character encoding
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;

    // Create blob with UTF-8 encoding
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });

    // Create download link
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', fullFilename);
    link.style.visibility = 'hidden';

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(url);
  };

  /**
   * Main export function
   * @param data - Array of data objects to export
   * @param options - Export options including filename and headers
   */
  const exportCSV = useCallback(async (data: any[], options: UseCSVExportOptions): Promise<void> => {
    try {
      setIsExporting(true);
      setError(null);

      if (!data || data.length === 0) {
        throw new Error('No data available to export');
      }

      if (!options.headers || options.headers.length === 0) {
        throw new Error('No headers specified for export');
      }

      // Convert to CSV
      const csvContent = convertToCSV(data, options.headers);

      // Download file
      downloadFile(csvContent, options.filename);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to export CSV';
      setError(errorMessage);
      console.error('CSV Export Error:', err);
    } finally {
      setIsExporting(false);
    }
  }, []);

  return {
    exportCSV,
    isExporting,
    error
  };
};

export default useCSVExport;